import React, { useEffect, useState, useCallback } from 'react';
import api, { fmtINR, fmtDate, API } from '@/lib/api';
import { Loading, EmptyState, SectionTitle, PaymentBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, CreditCard, FileText, Printer, BookOpen, FileDown, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { printCustomerStatement, printCustomerLedger } from '@/lib/statementPrint';
import { toast } from 'sonner';

export default function CustomerInvoices() {
  const [invoices, setInvoices] = useState(null);
  const [stMonth, setStMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ledger, setLedger] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);
  const load = useCallback(() => api.get('/invoices/mine').then((r) => setInvoices(r.data)), []);
  const loadLedger = useCallback(() => api.get('/customers/me/ledger').then((r) => setLedger(r.data)).catch(() => setLedger(null)), []);
  useEffect(() => { load(); loadLedger(); }, [load, loadLedger]);
  if (!invoices) return <Loading />;
  const pay = async (inv) => {
    try { await api.post('/payments', { invoice_id: inv.id, amount: inv.balance, method: 'UPI' }); toast.success('Payment recorded'); load(); loadLedger(); }
    catch (e) { toast.error('Payment failed'); }
  };
  const openPdf = (inv) => window.open(`${API}/invoices/${inv.id}/pdf`, '_blank');
  // One-tap consolidated monthly statement (A4 print)
  const printStatement = async () => {
    try {
      const r = await api.get('/statements/me', { params: { month: stMonth } });
      printCustomerStatement(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not build the statement'); }
  };
  // Download my ledger as PDF or Excel
  const downloadLedger = async (kind) => {
    setDlBusy(true);
    try {
      const r = await api.get(`/customers/me/ledger/${kind}`, { responseType: 'blob' });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-ledger.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Ledger ${kind === 'pdf' ? 'PDF' : 'Excel'} downloaded`);
    } catch (e) { toast.error('Could not download the ledger'); }
    finally { setDlBusy(false); }
  };
  return (
    <div className="space-y-4">
      <SectionTitle action={
        <span className="flex items-center gap-2">
          <Input type="month" value={stMonth} onChange={(e) => setStMonth(e.target.value)} className="h-9 w-40" data-testid="my-statement-month-input" />
          <Button size="sm" variant="secondary" onClick={printStatement} data-testid="my-statement-print-button"><Printer className="h-4 w-4 mr-1.5" />Monthly Statement</Button>
        </span>
      }>Invoices & Billing</SectionTitle>
      {invoices.length === 0 ? <EmptyState title="No invoices yet" desc="Invoices appear after delivery completion." icon={FileText} /> : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id} className={`rounded-xl border bg-card p-4 ${inv.payment_status === 'Overdue' ? 'border-l-4' : 'border-border'}`} style={inv.payment_status === 'Overdue' ? { borderLeftColor: 'hsl(var(--pay-Overdue))' } : {}} data-testid={`invoice-row-${inv.invoice_no}`}>
              <div className="flex items-center justify-between">
                <div><p className="font-medium">{inv.invoice_no}</p><p className="text-xs text-muted-foreground">{inv.order_no} · {fmtDate(inv.date)}</p></div>
                <PaymentBadge status={inv.payment_status} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div><span className="font-semibold">{fmtINR(inv.amount)}</span>{inv.balance > 0 && <span className="text-xs text-muted-foreground ml-2">Bal: {fmtINR(inv.balance)}</span>}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openPdf(inv)} data-testid="invoice-download-pdf-button"><Download className="h-4 w-4 mr-1" />PDF</Button>
                  {inv.balance > 0 && <Button size="sm" onClick={() => pay(inv)} data-testid={`pay-invoice-${inv.invoice_no}`}><CreditCard className="h-4 w-4 mr-1" />Pay</Button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My account ledger: outstanding + full transaction history */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="my-ledger-card">
        <div className="px-4 py-3 border-b border-border space-y-2">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-sm flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-primary" />My Account Ledger</p>
            {ledger && (
              <span className={`text-sm font-bold ${ledger.closing_balance > 0 ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`} data-testid="my-ledger-outstanding">
                {ledger.closing_balance > 0 ? `Outstanding: ${fmtINR(ledger.closing_balance)}` : 'No dues'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button size="sm" variant="secondary" onClick={() => ledger && printCustomerLedger(ledger)} disabled={!ledger} data-testid="my-ledger-print-button"><Printer className="h-4 w-4 mr-1" />Print A4</Button>
            <Button size="sm" variant="secondary" onClick={() => downloadLedger('pdf')} disabled={!ledger || dlBusy} data-testid="my-ledger-pdf-button">{dlBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}PDF</Button>
            <Button size="sm" variant="secondary" onClick={() => downloadLedger('excel')} disabled={!ledger || dlBusy} data-testid="my-ledger-excel-button">{dlBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}Excel</Button>
          </div>
        </div>
        {!ledger ? <div className="p-4"><Skeleton className="h-24 w-full" /></div> : (
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs text-muted-foreground">
                <tr>{['Date', 'Particulars', 'Debit', 'Credit', 'Balance'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(ledger.entries || []).map((e, i) => (
                  <tr key={`${e.type}-${e.date}-${i}`} className={`border-t border-border ${e.type === 'opening' ? 'bg-muted/40 font-medium' : ''}`} data-testid={`my-ledger-row-${e.type}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{e.date ? fmtDate(e.date) : '-'}</td>
                    <td className="px-3 py-2 max-w-[180px] sm:max-w-[260px] truncate">{e.particulars}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{e.debit ? fmtINR(e.debit) : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-emerald-600 dark:text-emerald-400">{e.credit ? fmtINR(e.credit) : '-'}</td>
                    <td className={`px-3 py-2 whitespace-nowrap font-semibold ${e.balance > 0 ? '' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtINR(e.balance)}</td>
                  </tr>
                ))}
                {(ledger.entries || []).length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No transactions yet. Your invoices and payments will appear here.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
